import {
  CountdownWidget,
  HonourRollWidget,
  ImageWidget,
  ImageWidgetSourceEnum,
  ItemTableCardType,
  ItemTableWidget,
  ThermometerWidget,
  TextWidget,
  TotalRaisedWidget,
  RaffleDrawWidget,
  RaffleWinnersWidget,
  NotificationWidget,
  NotifTypeEnum,
  SmartTextWidget,
  QrCodeWidget,
  TeamScoringWidget,
  VideoWidget,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'

export interface AuctionColors {
  primary_color: string
  accent_color: string
  background_color: string
  success_color?: string
  warn_color?: string
}

export const defaultText: Partial<TextWidget> = {
  text: 'This is a default text block.',
  font_family: '',
  font_size: 16,
  font_weight: 'normal',
  font_style: 'normal',
  text_align: 'left',
  text_decoration: 'none',
  line_height: 1.5,
  letter_spacing: 0,
  color: '',
  x: 50,
  x_unit: '%',
  y: 50,
  y_unit: '%',
}

export const defaultSmartText: Partial<SmartTextWidget> = {
  text: 'galabid.com/',
  font_family: '',
  font_size: 16,
  color: '',
  x: 50,
  x_unit: '%',
  y: 50,
  y_unit: '%',
}

export const getDefaultItemTableWidget = (colors?: AuctionColors): Partial<ItemTableWidget> => ({
  x: 0.5,
  x_unit: '%',
  y: 0.5,
  y_unit: '%',
  width_unit: '%',
  height_unit: '%',
  rotate_z: 0,
  rotate_z_unit: 'deg',
  columns: 5,
  rows: 1,
  slide_interval: 10,
  font_size: 12,
  card_type: ItemTableCardType.COMPACT_TABLE,
  transition: 'fade-in',
  image_contained: false,
  table_primary_color: colors?.accent_color ?? '#000000',
  table_accent_color: colors?.background_color ?? '#000000',
  table_background_color: colors?.primary_color ?? '#ffffff',
  hidden_items: [],
  width: 99,
  height: 50,
})

export const getDefaultTotalRaisedWidget = (colors?: AuctionColors): Partial<TotalRaisedWidget> => ({
  label: 'Total raised',
  total_raised: '0.00',
  track_bids: true,
  track_donations: true,
  track_purchases: true,
  track_raffles: true,
  track_ticket_sales: true,
  base_total_raised_enabled: false,
  font_family: '',
  font_size: 30,
  font_weight: '',
  text_align: '',
  height: null,
  height_unit: '%',
  opacity: 1,
  rotate_z: 0,
  rotate_z_unit: 'deg',
  width: null,
  width_unit: '%',
  x: 50,
  x_unit: '%',
  y: 50,
  y_unit: '%',
  z: 10,
})

export const getDefaultCountdownWidget = (colors?: AuctionColors): Partial<CountdownWidget> => ({
  font_family: '',
  font_weight: 'bold',
  font_size: 48,
  text_align: 'center',
  timer_type: '',
  letter_spacing: 1,
  timeout_text: 'Countdown finished',
  plus_overtime_label: true,
  end_on_auction_schedule_close: false,
  end: '',
  height: null,
  height_unit: '%',
  opacity: 1,
  rotate_z: 0,
  rotate_z_unit: 'deg',
  width: null,
  width_unit: '%',
  x: 50,
  x_unit: '%',
  y: 50,
  y_unit: '%',
  z: 10,
})

export const getDefaultHonourRollWidget = (colors?: AuctionColors): Partial<HonourRollWidget> => ({
  columns: 5,
  rows: 1,
  slide_interval: 10.0,
  show_item_name: true,
  show_item_alias: true,
  table_primary_color: colors?.accent_color ?? '#39a065',
  table_accent_color: colors?.primary_color ?? 'rgb(250, 250, 250)',
  font_size: 15,
  transition: 'scroll-top',
  latest_activities: 10,
  hidden_items: [],
  height: 90.0,
  height_unit: '%',
  opacity: 1.0,
  rotate_z: 0,
  rotate_z_unit: 'deg',
  width: 80.0,
  width_unit: '%',
  x: 9.6,
  x_unit: '%',
  y: 11.21,
  y_unit: '%',
  z: 5,
})

export const defaultRaffleDrawWidget: Partial<RaffleDrawWidget> = {
  width: 50,
  height: 15,
  height_unit: '%',
  width_unit: '%',
  font_size: 70,
  x: 25,
  y: 11,
  x_unit: '%',
  y_unit: '%',
  primary_color: 'rgba(255,255,255)',
  primary_text_color: 'rgba(0,0,0)',
}

export const getDefaultRaffleWinnersWidget = (colors?: AuctionColors): Partial<RaffleWinnersWidget> => ({
  width: 90,
  height: 60,
  height_unit: '%',
  width_unit: '%',
  font_size: 15,
  primary_color: colors?.primary_color ?? 'rgba(2,2,2)',
  primary_text_color: 'rgba(245,245,245)',
  accent_color: colors?.accent_color ?? 'rgba(245,245,245)',
  x: 5,
  y: 32,
  x_unit: '%',
  y_unit: '%',
})

export const getDefaultThermometerWidget = (colors?: AuctionColors): Partial<ThermometerWidget> => ({
  amount_raised: 0,
  fundraising_progress: 0,
  fundraising_target: 1000,
  track_pledges_only: false,
  base_total_raised_enabled: false,

  thermometer_color: colors?.primary_color ?? '#F44336',

  custom_image: null,

  border_color: '',
  border_style: '',
  border_width: 8,
  border_width_unit: 'px',
  border_radius: null,
  border_radius_unit: 'px',
  padding: null,
  padding_unit: 'px',
  box_shadow: '',

  height: 70,
  height_unit: '%',
  opacity: 1,
  rotate_z: 0,
  rotate_z_unit: 'deg',
  width: 13,
  width_unit: '%',
  x: 55.948,
  x_unit: '%',
  y: 12.249,
  y_unit: '%',
  z: 1,
})

export const getDefaultNotificationWidget = (colors?: AuctionColors): Partial<NotificationWidget> => ({
  width: 50,
  height: 50,
  height_unit: '%',
  width_unit: '%',
  font_size: 17,
  background_color: 'rgba(255,255,255)',
  primary_text_color: 'rgba(245,245,245)',
  notif_secondary_background_color: colors?.primary_color ?? 'rgba(57, 160, 101)',
  notif_secondary_color: 'rgba(255, 255, 255)',
  x: 25,
  x_unit: '%',
  y: 25,
  y_unit: '%',
  z: 10,
  notif_type: NotifTypeEnum.POPOUT_A,
  image_contained: false,
  show_bid_notifications: true,
  show_purchase_notifications: true,
  show_donation_notifications: true,
  show_custom_notifications: true,
})

export const getDefaultQrCodeWidget = (): Partial<QrCodeWidget> => ({
  x: 25,
  x_unit: '%',
  y: 25,
  y_unit: '%',
  width: 35,
  width_unit: '%',
  height: 60,
  height_unit: '%',
  z: 10,
  font_size: 16,
})

export const getDefaultTeamScoringWidget = (colors?: AuctionColors): Partial<TeamScoringWidget> => ({
  x: 0.5,
  x_unit: '%',
  y: 0.5,
  y_unit: '%',
  width_unit: '%',
  height_unit: '%',
  rotate_z: 0,
  rotate_z_unit: 'deg',
  columns: 1,
  rows: 5,
  slide_interval: 10,
  font_size: 12,
  card_type: 'standard_table',
  transition: 'fade-in',
  table_primary_color: colors?.accent_color ?? '#000000',
  table_accent_color: colors?.background_color ?? '#000000',
  table_background_color: colors?.primary_color ?? '#ffffff',
  hidden_teams: [],
  width: 99,
  height: 50,
})

export const getDefaultVideoWidget = (): Partial<VideoWidget> => ({
  x: 10,
  x_unit: '%',
  y: 10,
  y_unit: '%',
  width: 80,
  width_unit: '%',
  height: 60,
  height_unit: '%',
  z: 10,
  video_url: '',
})

export const getDefaultImageWidget = (): Partial<ImageWidget> => ({
  x: 75,
  x_unit: '%',
  y: 25,
  y_unit: '%',
  width: 20,
  width_unit: '%',
  height: null,
  height_unit: '%',
  image_source: ImageWidgetSourceEnum.Custom,
})
